import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from './SocketContext';
import {
    FaBell, FaThumbsUp, FaThumbsDown, FaKey, FaExternalLinkAlt,
    FaRegCommentDots, FaTasks, FaBullhorn, FaWrench,
    FaExclamationTriangle, FaUserShield, FaUnlockAlt, FaCheckDouble,
    FaFileSignature, FaTimes, FaSpinner, FaMoneyBillWave, FaBuilding, FaInfoCircle,
    FaCalendarCheck, FaStar, FaClipboardList, FaPaperPlane, FaRegSmile, FaCheckCircle
} from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';
import './NotificationsDropdown.css';
import './VozClienteModal.css';
import { useDataRefresher } from '../hooks/useDataRefresher';
import { useSupport } from '../context/SupportContext';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const playNotificationSound = () => {
    try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(error => console.log("Navegador bloqueou a reprodução automática do som.", error));
    } catch(e) {
        console.error("Erro ao tentar tocar o som da notificação", e);
    }
};

// Formata a data com segurança: evita o crash do parseISO(null/undefined),
// que acontecia com notificações efêmeras sem data_criacao válida.
const formatarDataRelativa = (dataCriacao) => {
    if (!dataCriacao) return '';
    try {
        return formatDistanceToNow(parseISO(dataCriacao), { addSuffix: true, locale: ptBR });
    } catch {
        return '';
    }
};

const getNotificationIcon = (type) => {
    switch (type) {
        case 'alerta_seguranca':              return { icon: <FaUserShield />,        className: 'icon-security-alert'    };
        case 'reabertura_mes_solicitacao':    return { icon: <FaUnlockAlt />,         className: 'icon-reabertura-alert'  };
        case 'reabertura_mes_decisao':        return { icon: <FaCheckDouble />,        className: 'icon-reabertura-decisao'};
        case 'despesa_pendente':              return { icon: <FaWrench />,            className: 'icon-despesa'           };
        case 'aprovacao_kb':
        case 'decisao_kb':                    return { icon: <FaTasks />,             className: 'icon-kb'                };
        case 'solicitacao_licenca':
        case 'decisao_licenca':               return { icon: <FaKey />,              className: 'icon-licenca'           };
        case 'anuncio':
        case 'anuncio_global':                return { icon: <FaBullhorn />,          className: 'icon-anuncio'           };
        case 'suporte_resposta':
        case 'suporte_status_change':         return { icon: <FaRegCommentDots />,    className: 'icon-suporte'           };
        case 'gmud_aprovada':                 return { icon: <FaThumbsUp />,          className: 'icon-gmud-approved'     };
        case 'gmud_rejeitada':                return { icon: <FaThumbsDown />,        className: 'icon-gmud-rejected'     };
        case 'aviso_senha':                   return { icon: <FaExclamationTriangle />,className: 'icon-password-warn'    };
        case 'tarefa_atribuida':              return { icon: <FaTasks />,             className: 'icon-kb'                };
        case 'aprovacao_pendente':            return { icon: <FaFileSignature />,     className: 'icon-aprovacao'         };
        // ✅ Lembrete de evento da Agenda Pessoal
        case 'agenda_lembrete':               return { icon: <FaCalendarCheck />,     className: 'icon-agenda-lembrete'   };
        case 'nps':                           return { icon: <FaStar />,             className: 'icon-nps'              };
        case 'pesquisa_uso':                  return { icon: <FaClipboardList />,     className: 'icon-pesquisa'         };
        default:                              return { icon: <FaBell />,              className: 'icon-default'           };
    }
};

// =========================================================================
// COMPONENTE DO MODAL GLOBAL DE APROVAÇÃO
// =========================================================================
const GlobalApprovalModal = ({ notificacao, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(true);
    const [dados, setDados] = useState(null);
    const [justificativa, setJustificativa] = useState('');
    const [processando, setProcessando] = useState(false);
    const [expandDetalhes, setExpandDetalhes] = useState(false);

    useEffect(() => {
        const fetchDetalhes = async () => {
            try {
                const { data } = await api.get(`/api/corporativo/aprovacoes/detalhes/${notificacao.link_id}`);
                setDados(data);
            } catch (error) {
                toast.error('Erro ao carregar detalhes do documento.');
                onClose();
            } finally {
                setLoading(false);
            }
        };
        fetchDetalhes();
    }, [notificacao.link_id, onClose]);

    const handleAcao = async (acao) => {
        const exigeJustificativa = dados?.instancia?.exige_justificativa === 1;
        if ((acao === 'reprovado' || exigeJustificativa) && !justificativa.trim()) {
            return toast.warn('A justificativa/parecer é obrigatória para esta ação.');
        }
        setProcessando(true);
        try {
            await api.post(`/api/corporativo/aprovacoes/${notificacao.link_id}/acao`, {
                acao,
                observacao: justificativa.trim() || null
            });
            toast.success(`Documento ${acao} com sucesso!`);
            onSuccess(notificacao.id);
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao processar aprovação.');
        } finally {
            setProcessando(false);
        }
    };

    if (loading) {
        return (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                    <FaSpinner className="fa-spin" size={34} color="#4f46e5" style={{ marginBottom: 15 }} />
                    <h3 style={{ color: '#1e293b', margin: '0 0 5px 0' }}>Buscando Documento</h3>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>Aguarde um instante...</p>
                </div>
            </div>
        );
    }

    if (!dados) return null;

    const po = dados.detalhes;
    const itens = dados.itens || [];
    const historico = dados.historico || [];
    const exigeJustificativa = dados.instancia?.exige_justificativa === 1;

    const dataEmissaoRaw = po?.data_emissao || po?.created_at || po?.data_criacao;
    const dataEmissaoFormatada = dataEmissaoRaw ? new Date(dataEmissaoRaw).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Data não registrada';
    const temDetalhesExtras = !!po?.cotacao_id || (po?.status && po?.status !== 'rascunho' && po?.status !== 'em_aprovacao');

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ width: '100%', maxWidth: '1100px', maxHeight: '92vh', backgroundColor: '#ffffff', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'entSlideUp 0.25s ease-out' }}>

                {/* HEADER */}
                <div style={{ padding: '20px 28px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 800 }}>
                        <FaFileSignature color="#4f46e5" size={22} /> Avaliação de Documento Oficial
                    </h2>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '6px', borderRadius: '50%', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#e2e8f0'} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <FaTimes size={20} />
                    </button>
                </div>

                {/* BODY */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr', gap: '28px', padding: '28px', overflowY: 'auto', flexGrow: 1, backgroundColor: '#fcfcfd' }}>

                    {/* COLUNA ESQUERDA */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                            <div>
                                <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#fef3c7', color: '#b45309', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '10px' }}>
                                    Fluxo: {dados.instancia?.nome_fluxo}
                                </span>
                                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.5rem', fontWeight: 800 }}>
                                    {String(dados.instancia?.entidade).toUpperCase()} #{dados.instancia?.entidade_id}
                                </h3>
                                {temDetalhesExtras && (
                                    <button
                                        onClick={() => setExpandDetalhes(!expandDetalhes)}
                                        style={{ background: expandDetalhes ? '#e2e8f0' : '#f1f5f9', border: 'none', color: '#475569', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', transition: 'background 0.2s' }}
                                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                                        onMouseOut={e => e.currentTarget.style.backgroundColor = expandDetalhes ? '#e2e8f0' : '#f1f5f9'}
                                    >
                                        <span style={{ fontSize: '1rem', lineHeight: 1 }}>{expandDetalhes ? '-' : '+'}</span>
                                        {expandDetalhes ? 'Ocultar detalhes' : 'Mais detalhes do pedido'}
                                    </button>
                                )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <small style={{ color: '#64748b', display: 'block', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>Valor Total Previsto</small>
                                <h2 style={{ margin: 0, color: '#059669', fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
                                    R$ {Number(po?.total_previsto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </h2>
                            </div>
                        </div>

                        {expandDetalhes && temDetalhesExtras && (
                            <div style={{ backgroundColor: '#f8fafc', padding: '16px 20px', borderRadius: '12px', border: '1px dashed #cbd5e1', animation: 'entSlideUp 0.2s ease-out' }}>
                                <h5 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '0.9rem' }}>Informações Complementares</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    {po?.cotacao_id && (
                                        <div>
                                            <small style={{ color: '#64748b', display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600 }}>ID da Cotação (RFQ)</small>
                                            <strong style={{ color: '#334155', fontSize: '0.9rem' }}>Vínculo com Cotação #{po.cotacao_id}</strong>
                                        </div>
                                    )}
                                    {po?.status && po?.status !== 'rascunho' && po?.status !== 'em_aprovacao' && (
                                        <div>
                                            <small style={{ color: '#64748b', display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600 }}>Status Original</small>
                                            <strong style={{ color: '#334155', fontSize: '0.9rem', textTransform: 'uppercase' }}>{po.status}</strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                                <small style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}><FaBuilding color="#94a3b8"/> Fornecedor</small>
                                <strong style={{ color: '#1e293b', display: 'block', marginTop: 8, fontSize: '1rem' }}>{po?.fornecedor_nome || 'Não Informado'}</strong>
                                <small style={{ color: '#94a3b8', display: 'block', marginTop: 2 }}>CNPJ: {po?.cnpj || 'N/I'}</small>
                            </div>
                            <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                                <small style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' }}><FaMoneyBillWave color="#94a3b8"/> Centro de Custo</small>
                                <strong style={{ color: '#1e293b', display: 'block', marginTop: 8, fontSize: '1rem' }}>{po?.centro_custo || 'Global (Sem Rateio)'}</strong>
                                <small style={{ color: '#94a3b8', display: 'block', marginTop: 2 }}>Classificação Financeira</small>
                            </div>
                        </div>

                        <div style={{ backgroundColor: '#fff', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: po?.justificativa_solicitante ? '16px' : '0' }}>
                                <div>
                                    <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600 }}>Condição de Pagamento</small>
                                    <strong style={{ color: '#334155', display: 'block', fontSize: '0.95rem', marginTop: '4px' }}>{po?.condicao_pagamento || 'Padrão / Não informada'}</strong>
                                </div>
                                <div>
                                    <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600 }}>Data de Emissão</small>
                                    <strong style={{ color: '#334155', display: 'block', fontSize: '0.95rem', marginTop: '4px' }}>{dataEmissaoFormatada}</strong>
                                </div>
                            </div>
                            {po?.justificativa_solicitante && (
                                <div style={{ paddingTop: '16px', borderTop: '1px dashed #cbd5e1' }}>
                                    <small style={{ color: '#4f46e5', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaInfoCircle /> Motivo / Justificativa do Solicitante
                                    </small>
                                    <div style={{ backgroundColor: '#f5f3ff', padding: '12px 16px', borderRadius: '8px', marginTop: '8px', color: '#334155', fontSize: '0.9rem', fontStyle: 'italic', border: '1px solid #ede9fe', lineHeight: '1.5' }}>
                                        "{po.justificativa_solicitante}"
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                <h4 style={{ margin: 0, color: '#334155', fontSize: '0.95rem', fontWeight: 700 }}>Itens da Solicitação</h4>
                            </div>
                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', fontSize: '0.9rem', textAlign: 'left', borderCollapse: 'collapse' }}>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 1 }}>
                                        <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                                            <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 600 }}>Descrição</th>
                                            <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>Qtd</th>
                                            <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 600, textAlign: 'right' }}>Unitário</th>
                                            <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 600, textAlign: 'right' }}>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itens.map((it, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                                <td style={{ padding: '12px 20px', color: '#334155' }}>{it.descricao}</td>
                                                <td style={{ padding: '12px 20px', color: '#334155', textAlign: 'center' }}>{Number(it.quantidade)} <span style={{fontSize: '0.8rem', color: '#94a3b8'}}>{it.unidade}</span></td>
                                                <td style={{ padding: '12px 20px', color: '#334155', textAlign: 'right', whiteSpace: 'nowrap' }}>R$ {Number(it.preco_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                <td style={{ padding: '12px 20px', color: '#0f172a', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>R$ {(Number(it.quantidade) * Number(it.preco_unitario)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* COLUNA DIREITA */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 4px 15px -3px rgba(0,0,0,0.05)' }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                Parecer Técnico
                                {exigeJustificativa ?
                                    <span style={{ color: '#dc2626', fontSize: '0.75rem', backgroundColor: '#fee2e2', padding: '2px 8px', borderRadius: '99px' }}>Obrigatório</span> :
                                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 400 }}>Opcional para aprovação</span>
                                }
                            </label>
                            <textarea
                                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical', fontSize: '0.95rem', outline: 'none', backgroundColor: '#f8fafc', color: '#334155', minHeight: '100px', fontFamily: 'inherit' }}
                                placeholder={exigeJustificativa ? "Descreva detalhadamente o motivo da sua decisão..." : "Adicione uma observação (opcional)..."}
                                value={justificativa}
                                onChange={(e) => setJustificativa(e.target.value)}
                                onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div>

                        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <h4 style={{ margin: '0 0 20px 0', color: '#1e293b', fontSize: '1rem', fontWeight: 700, borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                                Linha do Tempo (Etapa {dados.instancia.etapa_atual})
                            </h4>
                            <div style={{ flexGrow: 1, overflowY: 'auto', paddingRight: '5px' }}>
                                {historico.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
                                        <FaRegCommentDots size={24} style={{ opacity: 0.5, marginBottom: '10px' }}/>
                                        <p style={{ margin: 0, fontSize: '0.9rem' }}>Aguardando o primeiro parecer.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        {historico.map((h, i) => (
                                            <div key={i} style={{ display: 'flex', gap: '14px', position: 'relative', paddingBottom: i === historico.length - 1 ? '0' : '24px' }}>
                                                {i !== historico.length - 1 && (
                                                    <div style={{ position: 'absolute', left: '15px', top: '32px', bottom: '0', width: '2px', backgroundColor: '#e2e8f0', zIndex: 0 }}></div>
                                                )}
                                                <div style={{ position: 'relative', zIndex: 1, width: '32px', height: '32px', borderRadius: '50%', backgroundColor: h.acao === 'aprovado' ? '#ecfdf5' : '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `2px solid ${h.acao === 'aprovado' ? '#10b981' : '#ef4444'}` }}>
                                                    {h.acao === 'aprovado' ? <FaCheckDouble color="#059669" size={12} /> : <FaTimes color="#dc2626" size={12} />}
                                                </div>
                                                <div style={{ paddingTop: '4px', width: '100%' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                                        <strong style={{ color: '#0f172a', fontSize: '0.95rem' }}>{h.nome}</strong>
                                                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{h.data_acao ? new Date(h.data_acao).toLocaleDateString('pt-BR') : ''}</span>
                                                    </div>
                                                    <span style={{ color: h.acao === 'aprovado' ? '#059669' : '#dc2626', fontSize: '0.8rem', fontWeight: 600, display: 'block', marginTop: '2px', textTransform: 'uppercase' }}>
                                                        {h.acao === 'aprovado' ? 'Aprovou a solicitação' : 'Reprovou a solicitação'}
                                                    </span>
                                                    {h.observacao && (
                                                        <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#475569', fontSize: '0.85rem', position: 'relative' }}>
                                                            <div style={{ position: 'absolute', top: '-6px', left: '15px', width: '10px', height: '10px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0', transform: 'rotate(45deg)' }}></div>
                                                            <span style={{ position: 'relative', zIndex: 1, fontStyle: 'italic' }}>"{h.observacao}"</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <div style={{ backgroundColor: '#ffffff', padding: '20px 28px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexShrink: 0, boxShadow: '0 -4px 10px rgba(0,0,0,0.02)' }}>
                    <button onClick={onClose} disabled={processando} style={{ padding: '12px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569', fontWeight: 600, cursor: processando ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontSize: '0.95rem' }} onMouseOver={e => !processando && (e.currentTarget.style.backgroundColor = '#f8fafc')} onMouseOut={e => !processando && (e.currentTarget.style.backgroundColor = '#fff')}>
                        Cancelar
                    </button>
                    <button onClick={() => handleAcao('reprovado')} disabled={processando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontWeight: 600, cursor: processando ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontSize: '0.95rem', boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.2)' }} onMouseOver={e => !processando && (e.currentTarget.style.backgroundColor = '#dc2626')} onMouseOut={e => !processando && (e.currentTarget.style.backgroundColor = '#ef4444')}>
                        <FaTimes size={16}/> Reprovar
                    </button>
                    <button onClick={() => handleAcao('aprovado')} disabled={processando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#10b981', color: '#fff', fontWeight: 600, cursor: processando ? 'not-allowed' : 'pointer', transition: 'all 0.2s', fontSize: '0.95rem', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)' }} onMouseOver={e => !processando && (e.currentTarget.style.backgroundColor = '#059669')} onMouseOut={e => !processando && (e.currentTarget.style.backgroundColor = '#10b981')}>
                        <FaCheckDouble size={16}/> Aprovar Documento
                    </button>
                </div>
            </div>
        </div>
    );
};

// =========================================================================
// COMPONENTE PRINCIPAL (DROPDOWN)
// =========================================================================
// =========================================================================
// MODAL DE RESPOSTA — VOZ DO CLIENTE (NPS + Pesquisas de Uso)
// Abre ao clicar na notificação; busca a estrutura pública e grava a resposta.
// =========================================================================
const parseOpcoes = (o) => {
    if (!o) return [];
    if (Array.isArray(o)) return o;
    try { return JSON.parse(o); } catch { return []; }
};

const faixaNota = (n) => (n <= 6 ? 'detrator' : n <= 8 ? 'neutro' : 'promotor');

const VozClienteRespostaModal = ({ notificacao, onClose, onResponded }) => {
    const isNps = notificacao.tipo === 'nps';
    const endpoint = isNps
        ? `/api/system-hub/nps/campaigns/${notificacao.link_id}`
        : `/api/system-hub/surveys/${notificacao.link_id}`;

    const [loading, setLoading] = useState(true);
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [concluido, setConcluido] = useState(false);

    const [nota, setNota] = useState(null);
    const [comentario, setComentario] = useState('');
    const [respostas, setRespostas] = useState({});

    useEffect(() => {
        let ativo = true;
        (async () => {
            try {
                const { data } = await api.get(`${endpoint}/public`);
                if (!ativo) return;
                if (data.ja_respondida) setErro('Você já respondeu esta pesquisa. Obrigado!');
                else setDados(data);
            } catch (e) {
                if (!ativo) return;
                setErro(e.response?.data?.error || 'Não foi possível carregar esta pesquisa.');
            } finally {
                if (ativo) setLoading(false);
            }
        })();
        return () => { ativo = false; };
    }, [endpoint]);

    const setResp = (pid, val) => setRespostas((prev) => ({ ...prev, [pid]: val }));

    const enviarNps = async () => {
        if (nota === null) return toast.warn('Escolha uma nota de 0 a 10.');
        setEnviando(true);
        try {
            await api.post(`${endpoint}/answer`, { nota, comentario: comentario.trim() || null });
            setConcluido(true);
            setTimeout(() => onResponded(), 1600);
        } catch (e) {
            toast.error(e.response?.data?.error || 'Falha ao enviar sua resposta.');
            setEnviando(false);
        }
    };

    const enviarPesquisa = async () => {
        const faltando = dados.perguntas.filter(
            (p) => p.obrigatoria === 1 && !(respostas[p.id] ?? '').toString().trim()
        );
        if (faltando.length) return toast.warn('Responda todas as perguntas obrigatórias.');

        const payload = {
            respostas: Object.entries(respostas)
                .filter(([, v]) => (v ?? '').toString().trim() !== '')
                .map(([pid, valor]) => ({ pergunta_id: Number(pid), valor }))
        };
        setEnviando(true);
        try {
            await api.post(`${endpoint}/answer`, payload);
            setConcluido(true);
            setTimeout(() => onResponded(), 1600);
        } catch (e) {
            toast.error(e.response?.data?.error || 'Falha ao enviar suas respostas.');
            setEnviando(false);
        }
    };

    return (
        <div className="vc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="vc-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="vc-modal__glow" />
                <button className="vc-close" onClick={onClose} aria-label="Fechar"><FaTimes /></button>

                {loading && (
                    <div className="vc-state">
                        <FaSpinner className="fa-spin vc-state__spinner" />
                        <p>Carregando...</p>
                    </div>
                )}

                {!loading && concluido && (
                    <div className="vc-done">
                        <div className="vc-done__icon"><FaCheckCircle /></div>
                        <h3>Obrigado pelo seu feedback!</h3>
                        <p>Sua resposta foi registrada com sucesso.</p>
                    </div>
                )}

                {!loading && !concluido && erro && (
                    <div className="vc-done">
                        <div className="vc-done__icon vc-done__icon--muted"><FaRegSmile /></div>
                        <h3>{erro}</h3>
                        <button className="vc-submit vc-submit--ghost" onClick={onClose}>Fechar</button>
                    </div>
                )}

                {/* ---------- NPS ---------- */}
                {!loading && !concluido && !erro && dados && isNps && (
                    <>
                        <div className="vc-header">
                            <div className="vc-header__icon"><FaStar /></div>
                            <div className="vc-header__texts">
                                <div className="vc-header__title">{dados.titulo}</div>
                                <div className="vc-header__sub">Sua opinião nos ajuda a evoluir</div>
                            </div>
                        </div>
                        <div className="vc-body">
                            <p className="vc-pergunta">{dados.pergunta}</p>
                            <div className="vc-scale">
                                {Array.from({ length: 11 }, (_, n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`vc-scale__btn vc-scale__btn--${faixaNota(n)} ${nota === n ? 'is-selected' : ''}`}
                                        onClick={() => setNota(n)}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                            <div className="vc-scale__labels">
                                <span>0 — Não recomendo</span>
                                <span>Recomendo muito — 10</span>
                            </div>
                            <textarea
                                className="vc-comment"
                                placeholder="Quer contar o porquê da sua nota? (opcional)"
                                value={comentario}
                                onChange={(e) => setComentario(e.target.value)}
                                rows="3"
                            />
                        </div>
                        <div className="vc-footer">
                            <button className="vc-submit" onClick={enviarNps} disabled={enviando || nota === null}>
                                {enviando ? 'Enviando...' : <><FaPaperPlane /> Enviar resposta</>}
                            </button>
                        </div>
                    </>
                )}

                {/* ---------- PESQUISA ---------- */}
                {!loading && !concluido && !erro && dados && !isNps && (
                    <>
                        <div className="vc-header">
                            <div className="vc-header__icon vc-header__icon--survey"><FaClipboardList /></div>
                            <div className="vc-header__texts">
                                <div className="vc-header__title">{dados.titulo}</div>
                                {dados.descricao && <div className="vc-header__sub">{dados.descricao}</div>}
                            </div>
                        </div>
                        <div className="vc-body">
                            {dados.perguntas.map((p, idx) => (
                                <div key={p.id} className="vc-q">
                                    <label className="vc-q__label">
                                        <span className="vc-q__num">{idx + 1}</span>
                                        <span>{p.texto}{p.obrigatoria === 1 && <em className="vc-q__required">*</em>}</span>
                                    </label>

                                    {p.tipo === 'texto' && (
                                        <textarea
                                            className="vc-comment"
                                            rows="2"
                                            value={respostas[p.id] || ''}
                                            onChange={(e) => setResp(p.id, e.target.value)}
                                            placeholder="Sua resposta..."
                                        />
                                    )}

                                    {p.tipo === 'nota' && (
                                        <div className="vc-scale vc-scale--mini">
                                            {Array.from({ length: 11 }, (_, n) => (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    className={`vc-scale__btn vc-scale__btn--${faixaNota(n)} ${String(respostas[p.id]) === String(n) ? 'is-selected' : ''}`}
                                                    onClick={() => setResp(p.id, n)}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {p.tipo === 'sim_nao' && (
                                        <div className="vc-options">
                                            {['Sim', 'Não'].map((op) => (
                                                <button
                                                    key={op}
                                                    type="button"
                                                    className={`vc-option ${respostas[p.id] === op ? 'is-selected' : ''}`}
                                                    onClick={() => setResp(p.id, op)}
                                                >
                                                    {op}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {p.tipo === 'multipla' && (
                                        <div className="vc-options vc-options--col">
                                            {parseOpcoes(p.opcoes).map((op) => (
                                                <button
                                                    key={op}
                                                    type="button"
                                                    className={`vc-option ${respostas[p.id] === op ? 'is-selected' : ''}`}
                                                    onClick={() => setResp(p.id, op)}
                                                >
                                                    {op}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="vc-footer">
                            <button className="vc-submit" onClick={enviarPesquisa} disabled={enviando}>
                                {enviando ? 'Enviando...' : <><FaPaperPlane /> Enviar respostas</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default function NotificationsDropdown({ onProcessLicenseRequest, onShowAnuncio, onShowGlobalAnuncio }) {
    const navigate = useNavigate();
    const { perfil } = useAuth();
    const socket = useSocket();
    const [isOpen, setIsOpen] = useState(false);
    const [notificacoes, setNotificacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const dropdownRef = useRef(null);
    const { refreshKey, refreshData } = useDataRefresher();
    const { openSupportTicket } = useSupport();
    // A contagem anterior vive num ref, e não no state, para NÃO virar dependência
    // do useCallback. Antes, `lastUnreadCount` nas deps recriava fetchNotificacoes
    // a cada mudança de contagem → o setInterval de 20s era zerado e o listener de
    // socket re-registrado toda hora. Com o ref, o fetch é estável.
    const lastUnreadRef = useRef(0);
    const [aprovacaoAtiva, setAprovacaoAtiva] = useState(null);
    const [vozClienteAtiva, setVozClienteAtiva] = useState(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, []);

    const fetchNotificacoes = useCallback(async (isInitialLoad = false) => {
        if (!isInitialLoad) setLoading(true);
        try {
            const { data } = await api.get('/api/notificacoes');

            const dismissed = sessionStorage.getItem('pwdWarningDismissed') === 'true';
            const processedData = data.filter(n => {
                if (n.tipo === 'aviso_senha' && dismissed) return false;
                return true;
            });

            const currentUnreadCount = processedData.filter(n => !n.lida).length;
            // Só toca o som quando o número de não-lidas REALMENTE cresce em relação
            // ao último poll. Comparando contra o ref (e não contra um state que
            // entra nas deps), o loop de som do lembrete fantasma deixa de existir.
            if (!isInitialLoad && currentUnreadCount > lastUnreadRef.current) {
                playNotificationSound();
            }

            lastUnreadRef.current = currentUnreadCount;
            setNotificacoes(processedData);
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error("Erro ao buscar notificações:", error);
            }
        } finally {
            setLoading(false);
        }
    }, []); // sem deps: fetch estável, interval e socket não são recriados

    useEffect(() => {
        if (socket) {
            const handleNotificationUpdate = () => {
                console.log("🔔 Nova notificação recebida via Socket! Atualizando...");
                fetchNotificacoes(false);
            };
            socket.on('notification_update', handleNotificationUpdate);
            return () => { socket.off('notification_update', handleNotificationUpdate); };
        }
    }, [socket, fetchNotificacoes]);

    useEffect(() => {
        fetchNotificacoes(true);
        const interval = setInterval(() => fetchNotificacoes(false), 20000);
        return () => clearInterval(interval);
    }, [fetchNotificacoes, refreshKey]);

    const handleItemClick = async (notificacao) => {
        setIsOpen(false);

        if (notificacao.tipo === 'aprovacao_pendente') {
            setAprovacaoAtiva(notificacao);
            return;
        }
        if (notificacao.tipo === 'nps' || notificacao.tipo === 'pesquisa_uso') {
            setVozClienteAtiva(notificacao);
            return;
        }
        if (notificacao.tipo === 'anuncio' && onShowAnuncio) {
            onShowAnuncio(notificacao); return;
        }
        if (notificacao.tipo === 'anuncio_global' && onShowGlobalAnuncio) {
            onShowGlobalAnuncio(notificacao); return;
        }

        switch (notificacao.tipo) {
            case 'reabertura_mes_solicitacao':
            case 'reabertura_mes_decisao':
                navigate('/financeiro/fechamento'); break;
            case 'alerta_seguranca':
                navigate('/system-hub/forensics', { state: { targetUserId: notificacao.link_id } }); break;
            case 'despesa_pendente':
                navigate('/financeiro/gastos', { state: { highlightId: notificacao.link_id } }); break;
            case 'suporte_resposta':
            case 'suporte_status_change':
                openSupportTicket(notificacao.link_id); break;
            case 'solicitacao_licenca':
                if (perfil === 'admin_geral') handleOpenApprovalModal({ stopPropagation: () => {} }, notificacao.link_id);
                break;
            case 'decisao_kb':
            case 'aprovacao_kb':
                navigate('/base-conhecimento'); break;
            case 'gmud_aprovada':
            case 'gmud_rejeitada':
                navigate(`/suporte-admin/gmud/${notificacao.link_id}`); break;
            case 'aviso_senha':
                sessionStorage.setItem('pwdWarningDismissed', 'true');
                setNotificacoes(prev => prev.filter(n => n.tipo !== 'aviso_senha'));
                navigate('/perfil'); break;
            case 'tarefa_atribuida':
                navigate('/quadro-tarefas'); break;
            // ✅ Lembrete de agenda — navega para o dashboard onde o widget de countdown está visível
            case 'agenda_lembrete':
                navigate('/dashboard'); break;
            default:
                console.log("Ação não definida para este tipo de notificação.");
        }

        // Marca como lida no backend.
        // Agora o lembrete de agenda é PERSISTIDO com id numérico do banco, então
        // o antigo guard `typeof id === 'number'` já não o deixa de fora. Marcamos
        // qualquer notificação com id real, exceto o aviso de senha ('pwd-warn'),
        // que é efêmero e dispensado só via sessionStorage.
        if (!notificacao.lida && notificacao.id != null && notificacao.id !== 'pwd-warn') {
            try {
                await api.post(`/api/notificacoes/${notificacao.id}/lida`);
                refreshData();
                fetchNotificacoes(true);
            } catch (err) {
                console.error("Falha ao marcar notificação como lida");
            }
        }
    };

    const handleReaberturaDecision = async (event, notificacaoId, linkId, decisao) => {
        event.stopPropagation();
        let motivo = '';
        if (decisao === 'rejeitado') {
            motivo = prompt("Por favor, informe o motivo da reprovação da reabertura:");
            if (motivo === null || motivo.trim() === '') {
                toast.warn("A reprovação foi cancelada. O motivo é obrigatório.");
                return;
            }
        }
        try {
            await api.post(`/api/financeiro/reabrir-mes/decisao`, { notificacaoId, solicitacaoId: linkId, decisao, motivo });
            toast.success(`Solicitação de reabertura ${decisao === 'aprovado' ? 'aprovada' : 'rejeitada'}!`);
            refreshData(); fetchNotificacoes(true);
        } catch (error) { toast.error("Erro ao processar decisão de reabertura."); }
    };

    const handleKbDecision = async (event, artigoId, decisao) => {
        event.stopPropagation();
        let motivo = '';
        if (decisao === 'rejeitado') {
            motivo = prompt("Por favor, informe o motivo da rejeição:");
            if (motivo === null || motivo.trim() === '') {
                toast.warn("A rejeição foi cancelada pois o motivo é obrigatório."); return;
            }
        }
        try {
            const acao = decisao === 'aprovado' ? 'aprovado' : 'rejeitado';
            await api.put(`/api/notificacoes/kb/${artigoId}/decisao`, { decisao: acao, motivo });
            toast.success(`Artigo ${acao} com sucesso!`);
            refreshData();
        } catch (error) { toast.error("Falha ao processar a decisão."); }
    };

    const handleOpenApprovalModal = async (event, solicitacaoId) => {
        event.stopPropagation();
        try {
            const { data: requestDetails } = await api.get(`/api/system-hub/licensing-requests/${solicitacaoId}`);
            onProcessLicenseRequest(requestDetails);
            setIsOpen(false);
        } catch (error) { toast.error("Não foi possível carregar os detalhes da solicitação."); }
    };

    const handleMarkAllAsRead = async () => {
        try {
            let dismissedPwdWarning = false;
            if (notificacoes.some(n => n.tipo === 'aviso_senha' && !n.lida)) {
                sessionStorage.setItem('pwdWarningDismissed', 'true');
                dismissedPwdWarning = true;
            }
            await api.post('/api/notificacoes/marcar-todas-lidas');
            setNotificacoes(prev => prev.map(n => ({...n, lida: true})).filter(n => !(n.tipo === 'aviso_senha' && dismissedPwdWarning)));
            // Sincroniza o ref para o próximo poll não interpretar as agora-lidas
            // como "novas" e disparar o som indevidamente.
            lastUnreadRef.current = 0;
            toast.info("Todas as notificações foram marcadas como lidas.");
            refreshData();
        } catch (error) { toast.error("Não foi possível marcar todas como lidas."); }
    };

    const handleApprovalSuccess = async (notificacaoId) => {
        try {
            await api.post(`/api/notificacoes/${notificacaoId}/lida`);
            refreshData();
            fetchNotificacoes(true);
        } catch (err) { console.error("Falha ao marcar aprovação como lida", err); }
    };

    const unreadCount = notificacoes.filter(n => !n.lida).length;

    return (
        <div className="notifications-dropdown-container" ref={dropdownRef}>
            <button className="notifications-button" onClick={() => setIsOpen(prev => !prev)} aria-label="Abrir notificações">
                <FaBell className="fa-bell"/>
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notifications-dropdown">
                    <div className="notifications-header">
                        <strong>Notificações</strong>
                        {unreadCount > 0 && <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>Marcar todas como lidas</button>}
                    </div>
                    <div className="notifications-list">
                        {loading ? <p className="notification-item-empty">Carregando...</p> :
                         notificacoes.length === 0 ? <p className="notification-item-empty">Nenhuma notificação por aqui.</p> :
                         notificacoes.map(n => {
                             const { icon, className } = getNotificationIcon(n.tipo);
                             return (
                                 <div key={`${n.tipo}-${n.id}`} className={`notification-item ${!n.lida ? 'unread' : ''}`} onClick={() => handleItemClick(n)}>
                                     <div className={`notification-icon-container ${className}`}>
                                         {icon}
                                     </div>
                                     <div className="notification-content">
                                         <p className="notification-message" dangerouslySetInnerHTML={{ __html: n.mensagem }}></p>
                                         <p className="notification-date">
                                             {formatarDataRelativa(n.data_criacao)}
                                         </p>

                                         {/* KB */}
                                         {!n.lida && n.tipo === 'aprovacao_kb' && (
                                             <div className="notification-actions">
                                                 <button onClick={(e) => handleKbDecision(e, n.link_id, 'rejeitado')} className="action-reject"><FaThumbsDown /> Rejeitar</button>
                                                 <button onClick={(e) => handleKbDecision(e, n.link_id, 'aprovado')} className="action-approve"><FaThumbsUp /> Aprovar</button>
                                             </div>
                                         )}

                                         {/* Reabertura de Mês */}
                                         {!n.lida && perfil === 'admin_geral' && n.tipo === 'reabertura_mes_solicitacao' && (
                                             <div className="notification-actions reabertura-actions">
                                                 <button onClick={(e) => handleReaberturaDecision(e, n.id, n.link_id, 'rejeitado')} className="action-reject">
                                                     <FaThumbsDown /> Recusar
                                                 </button>
                                                 <button onClick={(e) => handleReaberturaDecision(e, n.id, n.link_id, 'aprovado')} className="action-approve">
                                                     <FaThumbsUp /> Autorizar
                                                 </button>
                                             </div>
                                         )}

                                         {/* Licença */}
                                         {!n.lida && perfil === 'admin_geral' && n.tipo === 'solicitacao_licenca' && (
                                             <div className="notification-actions license-actions">
                                                 <button onClick={(e) => handleOpenApprovalModal(e, n.link_id)} className="action-process">
                                                     <FaExternalLinkAlt /> Processar
                                                 </button>
                                             </div>
                                         )}

                                         {/* Aprovação de Fluxo */}
                                         {!n.lida && n.tipo === 'aprovacao_pendente' && (
                                             <div className="notification-actions">
                                                 <button
                                                    onClick={(e) => { e.stopPropagation(); setAprovacaoAtiva(n); setIsOpen(false); }}
                                                    className="action-process"
                                                    style={{ backgroundColor: '#f5f3ff', color: '#6d28d9', borderColor: '#ddd6fe' }}
                                                 >
                                                     <FaFileSignature /> Avaliar Documento
                                                 </button>
                                             </div>
                                         )}

                                         {/* ✅ Lembrete de Agenda Pessoal */}
                                         {!n.lida && n.tipo === 'agenda_lembrete' && (
                                             <div className="notification-actions">
                                                 <button
                                                     onClick={(e) => { e.stopPropagation(); handleItemClick(n); }}
                                                     className="action-process"
                                                     style={{ backgroundColor: '#ede9fe', color: '#7c3aed', borderColor: '#ddd6fe' }}
                                                 >
                                                     <FaCalendarCheck /> Ver na Agenda
                                                 </button>
                                             </div>
                                         )}
                                     </div>
                                 </div>
                             )
                         })
                        }
                    </div>
                </div>
            )}

            {/* Modal Global de Aprovação */}
            {aprovacaoAtiva && (
                <GlobalApprovalModal
                    notificacao={aprovacaoAtiva}
                    onClose={() => setAprovacaoAtiva(null)}
                    onSuccess={handleApprovalSuccess}
                />
            )}

            {/* Modal de Resposta — Voz do Cliente (NPS + Pesquisas) */}
            {vozClienteAtiva && (
                <VozClienteRespostaModal
                    notificacao={vozClienteAtiva}
                    onClose={() => setVozClienteAtiva(null)}
                    onResponded={() => { setVozClienteAtiva(null); refreshData(); fetchNotificacoes(true); }}
                />
            )}
        </div>
    );
}